import { httpsCallable } from 'firebase/functions';
import { err, ok, type Result } from '@shared/result';
import type { IAssistantSettingsRepository, RepositoryError } from '@application/ports';
import {
  MODEL_PROVIDERS,
  type AssistantKeyStatus,
  type AssistantModelTest,
  type AssistantProvider,
  type AssistantSettings,
  type ModelProvider,
} from '@domain/entities/AssistantSettings';
import { firebase } from '../firebase/client';
import { translateCallableError } from './support';

/**
 * La configuración del asistente contra el servidor: tres callables y ninguna
 * lectura directa. `whatsappSettings` no tiene regla en `firestore.rules`, así
 * que ni el superusuario la lee por su cuenta — y lo que hay ahí son las llaves
 * cifradas, que la callable tampoco devuelve.
 */

const record = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

const keyFrom = (raw: unknown): AssistantKeyStatus | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const data = record(raw);
  const updatedAtMs = data['updatedAtMs'];
  return {
    tail: text(data['tail']),
    updatedAt: typeof updatedAtMs === 'number' && updatedAtMs > 0 ? new Date(updatedAtMs) : null,
    updatedByName: text(data['updatedByName']),
    readable: data['readable'] === true,
  };
};

const isProvider = (value: unknown): value is AssistantProvider =>
  value === 'none' || value === 'openai' || value === 'anthropic';

/** Lo que contesta el servidor, leído sin creerle la forma. `null` si no la tiene. */
export const decodeAssistantSettings = (raw: unknown): AssistantSettings | null => {
  const data = record(raw);
  if (!isProvider(data['provider'])) return null;

  const keys = record(data['keys']);
  const models = record(data['models']);
  const whatsapp = record(data['whatsapp']);
  const each = <T>(pick: (provider: ModelProvider) => T): Record<ModelProvider, T> =>
    Object.fromEntries(MODEL_PROVIDERS.map((provider) => [provider, pick(provider)])) as Record<ModelProvider, T>;

  return {
    provider: data['provider'],
    keys: each((provider) => keyFrom(keys[provider])),
    models: each((provider) => text(models[provider])),
    canStoreKeys: data['canStoreKeys'] === true,
    whatsapp: {
      connected: whatsapp['connected'] === true,
      numberTail: text(whatsapp['numberTail']) || null,
      dryRun: whatsapp['dryRun'] === true,
    },
  };
};

const malformed = (what: string): RepositoryError => ({
  kind: 'malformed-document',
  path: what,
  detail: 'El servidor contestó algo que no se pudo leer.',
});

export class FirestoreAssistantSettingsRepository implements IAssistantSettingsRepository {
  private async call(name: string, payload: unknown): Promise<Result<AssistantSettings, RepositoryError>> {
    try {
      const { functions } = firebase();
      const { data } = await httpsCallable<unknown, unknown>(functions, name)(payload);
      const settings = decodeAssistantSettings(data);
      return settings === null ? err(malformed(name)) : ok(settings);
    } catch (cause) {
      return err(translateCallableError(cause, name));
    }
  }

  get(): Promise<Result<AssistantSettings, RepositoryError>> {
    return this.call('getAssistantSettings', {});
  }

  setProvider(provider: AssistantProvider): Promise<Result<AssistantSettings, RepositoryError>> {
    return this.call('saveAssistantSettings', { provider });
  }

  setKey(provider: ModelProvider, key: string | null): Promise<Result<AssistantSettings, RepositoryError>> {
    return this.call('saveAssistantSettings', { keys: { [provider]: key } });
  }

  async test(provider: ModelProvider): Promise<Result<AssistantModelTest, RepositoryError>> {
    try {
      const { functions } = firebase();
      const { data } = await httpsCallable<unknown, unknown>(functions, 'testAssistantModel')({ provider });
      const answer = record(data);
      return ok({ ok: answer['ok'] === true, detail: text(answer['detail']) });
    } catch (cause) {
      return err(translateCallableError(cause, 'testAssistantModel'));
    }
  }
}
